import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { signOut } from "../lib/auth-client.ts";
import { useSession } from "../lib/session.tsx";
import { deleteAccount, ownedOrgsBlockingDeletion, updateProfileName } from "../lib/team.ts";
import { toast } from "../components/Toast.tsx";

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Delete-account modal (block-until-resolved)
// ---------------------------------------------------------------------------

function DeleteAccountModal({ onClose, onDeleted }: { onClose: () => void; onDeleted: () => void }) {
  const blocking = ownedOrgsBlockingDeletion();
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDelete = blocking.length === 0 && confirm.trim().toLowerCase() === "delete";

  async function handleDelete() {
    setLoading(true);
    setError(null);
    try {
      await deleteAccount();
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete account.");
      setLoading(false);
    }
  }

  return createPortal(
    <div
      className="modal-root open"
      style={{ position: "fixed", inset: 0, zIndex: 100, display: "block" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-scrim" style={{ position: "absolute", inset: 0, background: "oklch(8% 0.01 235 / 0.6)", backdropFilter: "blur(3px)" }} />
      <div className="modal" role="dialog" aria-modal="true" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", opacity: 1 }}>
        <div className="modal-head">
          <div>
            <h3 style={{ color: "var(--danger)" }}>Delete account</h3>
            <p className="sub">This permanently deletes your account and cannot be undone.</p>
          </div>
          <button className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        {error && <div className="error-banner">{error}</div>}
        <div className="modal-body">
          {blocking.length > 0 ? (
            <>
              <p className="sub">
                You're the owner of {blocking.length} organization{blocking.length !== 1 ? "s" : ""} with other members.
                Transfer ownership or delete {blocking.length !== 1 ? "them" : "it"} before deleting your account:
              </p>
              <ul style={{ display: "flex", flexDirection: "column", gap: "8px", listStyle: "none" }}>
                {blocking.map((o) => (
                  <li key={o.id} className="row" style={{ justifyContent: "space-between", border: "1px solid var(--border)", borderRadius: "var(--radius-xs)", padding: "10px 12px" }}>
                    <span><strong>{o.name}</strong> <span className="muted">· {o.members} members</span></span>
                    <span className="row gap-sm">
                      <Link className="btn btn-sm btn-ghost" to={`/dashboard/${o.id}/team`} onClick={onClose}>Transfer</Link>
                      <Link className="btn btn-sm btn-ghost" to={`/dashboard/${o.id}/organizations`} onClick={onClose}>Manage</Link>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="field">
              <p className="sub" style={{ marginBottom: 10 }}>
                This permanently erases your account and any organization you solely own, including its API keys,
                settings, and usage data. See{" "}
                <a href="https://currency-core.com/privacy#data-deletion" target="_blank" rel="noreferrer">how we handle data deletion</a>.
              </p>
              <label htmlFor="confirmDelete">Type <strong style={{ color: "var(--text)" }}>delete</strong> to confirm</label>
              <input
                id="confirmDelete"
                className="input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="delete"
                autoFocus
                autoComplete="off"
              />
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" disabled={!canDelete || loading} onClick={handleDelete}>
            {loading ? <><span className="spinner" />Deleting…</> : "Delete account"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Profile page
// ---------------------------------------------------------------------------

export function ProfilePage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const user = session?.user;

  const [name, setName] = useState(user?.name ?? "");
  const [saving, setSaving] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  async function saveName(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await updateProfileName(trimmed);
      toast("Profile updated", true);
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  function onDeleted() {
    // Account gone (or mock session cleared): go to a fresh start.
    navigate("/login", { replace: true });
  }

  const initials = (user?.name ?? "U").trim()[0]?.toUpperCase() ?? "U";

  return (
    <div className="animate-fade-in">
      <div className="page-head rise">
        <div>
          <h1>Account</h1>
          <p className="sub">Manage your personal profile and account settings.</p>
        </div>
        {orgId && (
          <div className="page-head-actions">
            <Link className="btn btn-ghost" to={`/dashboard/${orgId}`}>Back to dashboard</Link>
          </div>
        )}
      </div>

      {/* Profile card */}
      <div className="card rise" style={{ padding: "20px", maxWidth: "560px" }}>
        <div className="row gap-sm" style={{ marginBottom: "18px" }}>
          <span className="avatar" style={{ width: "44px", height: "44px", fontSize: "16px" }}>{initials}</span>
          <div>
            <div style={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>{user?.name}</div>
            <div className="muted" style={{ fontSize: "13px" }}>{user?.email}</div>
          </div>
        </div>

        <form onSubmit={saveName} className="grid" style={{ gap: "16px" }}>
          <div className="field">
            <label htmlFor="profName">Display name</label>
            <input id="profName" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <label htmlFor="profEmail">Email</label>
            <input id="profEmail" className="input" value={user?.email ?? ""} readOnly disabled />
            <span className="help">Your email address can't be changed.</span>
          </div>
          <div className="row gap-sm">
            <button type="submit" className="btn btn-primary" disabled={saving || name.trim() === (user?.name ?? "")}>
              {saving ? <><span className="spinner" />Saving…</> : "Save changes"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={handleSignOut}>Sign out</button>
          </div>
        </form>
      </div>

      {/* Danger zone */}
      <div className="card rise" style={{ padding: "20px", maxWidth: "560px", marginTop: "18px", borderColor: "oklch(72% 0.18 25 / 0.35)" }}>
        <h2 style={{ color: "var(--danger)", fontSize: "16px", marginBottom: "6px" }}>Danger zone</h2>
        <p className="sub" style={{ marginBottom: "14px" }}>
          Permanently delete your account and remove yourself from all organizations. This cannot be undone.
        </p>
        <button className="btn btn-danger" onClick={() => setShowDelete(true)}>Delete account</button>
      </div>

      {showDelete && <DeleteAccountModal onClose={() => setShowDelete(false)} onDeleted={onDeleted} />}
    </div>
  );
}
